/**
 * Supabase client (browser).
 *
 * IMPORTANT — production fetch-interceptor workaround (Feb 2026):
 *   The Emergent deployment platform auto-injects an error-monitoring
 *   script (`https://assets.emergent.sh/scripts/emergent-main.js`)
 *   into every deployed app. That script patches `window.fetch` and,
 *   on any non-2xx response, calls `response.text()` on the original
 *   Response object WITHOUT cloning it first. By the time Supabase's
 *   auth-js SDK tries to read the same body via `result.json()`, the
 *   stream is already drained and the SDK throws:
 *
 *     "Failed to execute 'json' on 'Response': body stream already read"
 *
 *   Surface symptom: Sign-In appears to "do nothing" for valid users
 *   (the success-path 200 was unaffected, but ANY 4xx — bad password,
 *   rate-limit, expired session — surfaced the cryptic stream error).
 *
 *   Fix: hand Supabase a `fetch` reference taken from a fresh iframe
 *   context. The iframe's `fetch` is the genuine browser native, since
 *   the platform's monitoring script only patches the top window.
 *   We do NOT touch `window.fetch` — every other consumer (admin
 *   dashboard, vault uploads, agent SSE) keeps the platform's
 *   monitoring intact. Only Supabase auth/storage/realtime traffic
 *   bypasses it, and that traffic is the only thing that needs to
 *   reliably read the response body itself.
 */
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn('Supabase credentials not configured. Auth features will be disabled.');
}

/**
 * Returns an unpatched native fetch by stealing it from a hidden
 * iframe. Falls back to `window.fetch` if anything goes wrong (SSR,
 * sandboxing, JSDOM, etc.).
 */
function getUnpatchedFetch() {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    // SSR / Node — return whatever fetch is on the global, if any.
    return typeof fetch === 'function' ? fetch : undefined;
  }
  try {
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.setAttribute('aria-hidden', 'true');
    iframe.setAttribute('tabindex', '-1');
    (document.body || document.documentElement).appendChild(iframe);
    const iframeFetch = iframe.contentWindow && iframe.contentWindow.fetch;
    iframe.remove();
    if (typeof iframeFetch === 'function') {
      // Bind to the top window so cookies, storage and CSP scope are
      // identical to a regular page-context fetch.
      return iframeFetch.bind(window);
    }
  } catch {
    /* fall through */
  }
  return window.fetch ? window.fetch.bind(window) : undefined;
}

const nativeFetch = getUnpatchedFetch();

export const supabase = supabaseUrl && supabaseAnonKey
  ? createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
        // Magic-links from /auth/v1/admin/generate_link arrive in the URL
        // fragment (#access_token=...&refresh_token=...&type=magiclink).
        // supabase-js v2.40+ defaults to PKCE which expects ?code=...,
        // so we explicitly opt back into the implicit/hash flow here.
        flowType: 'implicit',
      },
      // Pass the unpatched fetch to every Supabase sub-client (auth-js,
      // postgrest, storage, realtime). Without this, the platform's
      // error-monitoring `window.fetch` wrapper drains response bodies
      // on 4xx, breaking sign-in error handling on production.
      global: nativeFetch ? { fetch: nativeFetch } : undefined,
    })
  : null;

export const isSupabaseConfigured = () => !!supabase;
